import React, { useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Stack,
  Paper,
} from '@mui/material';

import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import EditIcon from '@mui/icons-material/Edit';

import PageLayout from '../../components/layout/PageLayout';
import { useGetInvoiceByIdQuery } from '../../store/api';
import { toast } from 'react-toastify';
import { buildInvoiceRelationParams } from './invoiceApiHelpers';
import Can from '../../components/permissions/Can';
import InvoiceDetailContent, { invoiceStatusChip } from './InvoiceDetailContent';
import { isInvoiceStatusPartial } from './invoiceFieldParsers';

const ViewInvoice = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const {
    data,
    isLoading: loading,
    isError,
    error,
  } = useGetInvoiceByIdQuery(
    { id, params: buildInvoiceRelationParams() },
    { skip: !id }
  );

  const inv = data?.data ?? data ?? null;

  useEffect(() => {
    if (isError) {
      toast.error(error?.data?.message || 'Failed to load invoice');
      navigate('/invoices');
    }
  }, [isError, error, navigate]);

  if (loading || !inv) {
    return (
      <PageLayout>
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight={320}
        >
          <CircularProgress />
        </Box>
      </PageLayout>
    );
  }

  return (
    <PageLayout>
      <Container maxWidth="lg" sx={{ py: 4 }}>
        {/* Top Actions */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 3,
            flexWrap: 'wrap',
            gap: 2,
          }}
        >
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/invoices')}
            variant="outlined"
          >
            Back
          </Button>

          <Can permission="invoice-edit">
            {!isInvoiceStatusPartial(inv) && (
              <Button
                variant="contained"
                startIcon={<EditIcon />}
                onClick={() => navigate(`/invoices/edit/${id}`)}
              >
                Edit Invoice
              </Button>
            )}
          </Can>
        </Box>

        {/* Header Card */}
        <Paper
          elevation={0}
          sx={{
            p: 4,
            mb: 3,
            borderRadius: 5,
            border: '1px solid',
            borderColor: 'divider',
            background:
              'linear-gradient(135deg, rgba(25,118,210,0.08), rgba(25,118,210,0.02))',
          }}
        >
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={3}
            alignItems={{ xs: 'flex-start', sm: 'center' }}
          >
            <Box
              sx={{
                width: 72,
                height: 72,
                borderRadius: 3,
                bgcolor: 'primary.main',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white',
              }}
            >
              <ReceiptLongIcon />
            </Box>

            <Box sx={{ flex: 1 }}>
              <Typography variant="h4" sx={{ fontWeight: 700 }}>
                Invoice Details
              </Typography>

              <Typography variant="body2" color="text.secondary">
                View full invoice information and payment status
              </Typography>
            </Box>

            <Box>{invoiceStatusChip(inv)}</Box>
          </Stack>
        </Paper>

        {/* Content Card */}
        <Card
          elevation={0}
          sx={{
            borderRadius: 5,
            border: '1px solid',
            borderColor: 'divider',
          }}
        >
          <CardContent sx={{ p: 4 }}>
            <InvoiceDetailContent inv={inv} />
          </CardContent>
        </Card>
      </Container>
    </PageLayout>
  );
};

export default ViewInvoice;